import { Injectable, WritableSignal, signal, inject } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { Breadcrumb } from '../../../models/breadcrumb';
import { TournamentService } from '../../';

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private _crumbs: WritableSignal<Breadcrumb[]> = signal([]);
  readonly breadcrumbs = this._crumbs.asReadonly();

  private tournamentService = inject(TournamentService);

  constructor(private router: Router, private route: ActivatedRoute) {
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => this.build(this.route.root))
    ).subscribe(crumbs => this._crumbs.set(crumbs));
  }

  private build(
    node: ActivatedRoute,
    url: string = '',
    crumbs: Breadcrumb[] = []
  ): Breadcrumb[] {
    const data = node.snapshot.data;
    const routeData = node.routeConfig?.data ?? {};
    const segment = node.snapshot.url.map(s => s.path).join('/');
    const nextUrl = segment ? `${url}/${segment}` : url;

    const breadcrumbLabel: string | undefined = routeData['breadcrumb'] ?? data?.['breadcrumb'];

    if (breadcrumbLabel) {
      // Use the breadcrumb label and apply token replacement like navbar
      let label = breadcrumbLabel;

      // Apply round number token replacement
      if (label.includes(':roundNumber')) {
        const selectedRound = this.tournamentService.selectedRoundSignal();
        const roundNumber = selectedRound?.number?.toString() || '';
        label = label.replace(':roundNumber', roundNumber);
      }

      // Override the label with resolved data when configured via dynamicKey/dynamicProp
      const dynamicLabel = this.resolveDynamicLabel(routeData, data);
      if (dynamicLabel) {
        label = dynamicLabel;
      }

      crumbs.push({ label, url: nextUrl });
    }

    if (node.firstChild) {
      return this.build(node.firstChild, nextUrl, crumbs);
    }

    // Remove any consecutive duplicates (e.g., 'Tournaments' twice)
    const finalCrumbs = crumbs.filter((c, i, arr) => i === 0 || c.label !== arr[i - 1].label);

    // Add "Home" at the beginning if we have breadcrumbs and we're not already at home/dashboard
    if (finalCrumbs.length > 0 && !finalCrumbs[0].label.toLowerCase().includes('dashboard') && !finalCrumbs[0].label.toLowerCase().includes('home')) {
      finalCrumbs.unshift({ label: 'Home', url: '/dashboard' });
    }

    return finalCrumbs;
  }

  private resolveDynamicLabel(routeData: Record<string, any>, resolvedData: Record<string, any>): string | undefined {
    const dynamicKey = routeData['dynamicKey'];
    if (!dynamicKey) {
      return undefined;
    }

    const dynamicSource = resolvedData[dynamicKey];
    if (!dynamicSource) {
      return undefined;
    }

    const dynamicProp = routeData['dynamicProp'];

    if (Array.isArray(dynamicProp)) {
      const values = dynamicProp
        .map(prop => this.getNestedValue(dynamicSource, prop))
        .filter(value => value !== undefined && value !== null && value !== '');

      if (values.length === 0) {
        return undefined;
      }

      return values.join(' ').trim();
    }

    if (typeof dynamicProp === 'string' && dynamicProp.length > 0) {
      const value = this.getNestedValue(dynamicSource, dynamicProp);
      return this.normalizeLabelValue(value);
    }

    return this.normalizeLabelValue(dynamicSource);
  }

  private getNestedValue(source: any, path: string): any {
    return path
      .split('.')
      .reduce((acc, key) => (acc !== undefined && acc !== null ? acc[key] : undefined), source);
  }

  private normalizeLabelValue(value: any): string | undefined {
    if (value === undefined || value === null) {
      return undefined;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : undefined;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return value.toString();
    }

    return undefined;
  }
}
